import { useEffect, useRef, useState } from "react";
import { useField } from "formik";
import { Input } from "reactstrap";
import classNames from "classnames";
import { useTranslation } from "react-i18next";

import EmojiPicker from "../EmojiPicker";
import { MAX_MESSAGE_LENGTH } from "@validation/lengthConstants";
import { getMaxLength } from "@helpers/validation";
import { enterCode } from "@const/chat";

import styles from "./index.module.scss";

const FormikChatInput = ({
  name = "",
  className = "",
  emojiClassName,
  textareaClassName,
  autoFocus,
  disabled,
  max = MAX_MESSAGE_LENGTH,
  rows = 1,
  placeholder = "WRITE_A_MESSAGE",
  onSubmit,
  isSubmitDisabled,
  maxHeight = 250,
  minHeight,
  pickerPlacement,
  pickerOffset,
}) => {
  const { t } = useTranslation("placeholders");
  const inputRef = useRef(null);
  const [textAreaHeight, setTextAreaHeight] = useState("auto");

  // eslint-disable-next-line no-unused-vars
  const [{ onChange, value, ...field }, _meta, { setValue }] = useField({
    name,
    type: "text",
  });

  const handleChange = (e) => {
    onChange(e);
  };

  const isTextAreaScrollable = inputRef?.current?.scrollHeight > textAreaHeight;

  useEffect(() => {
    if (value !== "") {
      if (inputRef?.current?.scrollHeight < maxHeight) {
        const scrollHeightPx = `${inputRef?.current?.scrollHeight}px`;
        setTextAreaHeight(scrollHeightPx);
      } else {
        setTextAreaHeight(maxHeight);
      }
    } else {
      setTextAreaHeight("auto");
    }
  }, [value]);

  const maxCalculatedLength = getMaxLength(value, max);

  useEffect(() => {
    if (!value) return;
    if (value.length <= maxCalculatedLength) return;
    setValue(value.slice(0, maxCalculatedLength));
  }, [value, setValue, maxCalculatedLength]);

  const handleOnEnterSubmit = (e) => {
    if (e.keyCode === enterCode && !e.shiftKey && !isSubmitDisabled) {
      e.preventDefault();
      onSubmit();
      inputRef?.current?.focus();
    }
  };

  return (
    <>
      <div className={classNames("d-flex", className, styles.formikChat)}>
        <Input
          {...field}
          autoFocus={autoFocus}
          disabled={disabled}
          innerRef={inputRef}
          name={name}
          maxLength={maxCalculatedLength}
          className={classNames(
            "text-emoji-regular",
            isTextAreaScrollable ? "overflow-y-scroll" : "overflow-hidden",
            styles.formikChatInput,
            textareaClassName,
          )}
          type="textarea"
          value={value}
          onChange={handleChange}
          rows={rows}
          style={{
            height: textAreaHeight,
            minHeight,
          }}
          placeholder={t(placeholder)}
          onKeyDown={handleOnEnterSubmit}
        />
        <div
          className={classNames(
            "h-fit-content",
            styles.iconContainer,
            emojiClassName,
          )}
        >
          <EmojiPicker
            disabled={disabled}
            emojiClassName={styles.formikChatEmojiButton}
            value={value}
            setValue={setValue}
            inputRef={inputRef}
            placement={pickerPlacement}
            offset={pickerOffset}
          />
        </div>
      </div>
    </>
  );
};

export default FormikChatInput;
